import { action, computed, observable, runInAction } from 'mobx';

import facility from 'app/facility';

import { BamboraCheckout } from '../../../../common';

const {
  settings: {
    currency,
    paypalClientId,
    stripePublishKey,
    stripeSepaEnabled,
    supportsBambora,
  },
} = facility;

const DEFAULT_PAYMENT_ERROR_MESSAGE = i18n.gettext(
  'The transaction could not be completed. Please contact us directly.'
);

export class TicketsBaseFormStore {
  INITIAL = 'Initial';
  CONFIRMATION = 'Confirmation';
  PURCHASE_ERROR = 'Purchase error';
  paymentTypes = {
    sepa: 'SEPA-DD',
    credit: 'CCARD',
    paypal: 'PAYPAL',
    bambora: 'BAMBORA',
  };

  @observable accessor step = this.INITIAL;
  @observable.shallow accessor formValues = new Map();
  @observable.shallow accessor errors = new Map();
  @observable accessor stripe;
  @observable accessor isSavingPayment = false;
  @observable accessor serverError = null;
  @observable accessor isCheckingOut = false;
  view = null;

  @computed get hasErrors() {
    return this.errors.size > 0;
  }

  @computed get disableSaveButton() {
    return (
      this.view.purchaseDataStore.isPosting ||
      this.hasErrors ||
      this.isSavingPayment ||
      this.isCheckingOut
    );
  }

  @action setFormValue = (key, value) => {
    this.formValues.set(key, value);
    this.errors.delete(key);
    this.validateFieldValue(key);
    this.serverError = null;
  };

  @action setError = (key, value) => {
    this.errors.set(key, value);
  };

  @action validateFieldValue = (field) => {
    const value = this.formValues.get(field) || '';
    switch (field) {
      case 'name':
        if (value.length > 512) {
          this.errors.set(
            field,
            i18n.sprintf(
              i18n.gettext('Name should not exceed %d characters'),
              512
            )
          );
        } else {
          this.errors.delete(field);
        }
        break;
      case 'surname':
        if (value.length > 512) {
          this.errors.set(
            field,
            i18n.sprintf(
              i18n.gettext('Surname should not exceed %d characters'),
              512
            )
          );
        } else {
          this.errors.delete(field);
        }
        break;
      case 'phone':
        if (value.length > 128) {
          this.errors.set(
            field,
            i18n.sprintf(
              i18n.gettext('Phone should not exceed %d characters'),
              128
            )
          );
        } else {
          this.errors.delete(field);
        }
        break;
      case 'comment':
        if (value.length > 4096) {
          this.errors.set(
            field,
            i18n.sprintf(
              i18n.gettext('Comment should not exceed %d characters'),
              4096
            )
          );
        } else {
          this.errors.delete(field);
        }
        break;
      case 'notes':
        if (value.length > 4096) {
          this.errors.set(
            field,
            i18n.sprintf(
              i18n.gettext('Notes should not exceed %d characters'),
              4096
            )
          );
        } else {
          this.errors.delete(field);
        }
        break;
      case 'sender':
        if (value.length > 20) {
          this.errors.set(
            field,
            i18n.sprintf(
              i18n.gettext('Sender should not exceed %d characters'),
              20
            )
          );
        } else {
          this.errors.delete(field);
        }
        break;
      case 'recipient':
        if (value.length > 20) {
          this.errors.set(
            field,
            i18n.sprintf(
              i18n.gettext('Recipient should not exceed %d characters'),
              20
            )
          );
        } else {
          this.errors.delete(field);
        }
        break;
      case 'greeting_message':
        if (value?.split('\n').length > 8) {
          this.errors.set(
            field,
            i18n.sprintf(
              i18n.gettext('The message should not exceed %d lines'),
              8
            )
          );
        } else if (value?.length > 256) {
          this.errors.set(
            field,
            i18n.sprintf(
              i18n.gettext('The message should not exceed %d characters'),
              256
            )
          );
        } else {
          this.errors.delete(field);
        }
        break;
      case 'recipient_name':
        if (value.length > 64) {
          this.errors.set(
            field,
            i18n.sprintf(
              i18n.gettext('Recipient name should not exceed %d characters'),
              64
            )
          );
        } else {
          this.errors.delete(field);
        }
        break;
      case 'recipient_surname':
        if (value.length > 64) {
          this.errors.set(
            field,
            i18n.sprintf(
              i18n.gettext('Recipient surname should not exceed %d characters'),
              64
            )
          );
        } else {
          this.errors.delete(field);
        }
        break;
    }
  };

  @computed get isSubmitting() {
    return this.view.purchaseDataStore.isBusy;
  }

  @computed get hideBuyButton() {
    return (
      this.step === this.PAYMENT &&
      this.formValues.get('payment_type') === this.paymentTypes.paypal
    );
  }

  @computed get payPalPaymentReady() {
    return (
      this.step === this.PAYMENT &&
      this.formValues.get('payment_type') === this.paymentTypes.paypal &&
      !!this.formValues.get('token')
    );
  }

  @action setFormDefaults = () => {
    this.formValues.clear();
    this.setCustomFormDefaults();
    if (stripePublishKey) {
      this.formValues.set(
        'payment_type',
        stripeSepaEnabled ? this.paymentTypes.sepa : this.paymentTypes.credit
      );
    } else if (supportsBambora) {
      this.formValues.set('payment_type', this.paymentTypes.bambora);
    } else if (paypalClientId) {
      this.formValues.set('payment_type', this.paymentTypes.paypal);
    }
    this.errors.clear();
    this.serverError = null;
  };

  @action
  validatePayment = () => {
    switch (this.formValues.get('payment_type')) {
      case this.paymentTypes.sepa: {
        if (!this.formValues.get('owner_name')) {
          this.setError('owner_name', i18n.gettext('Please input owner name.'));
        } else if (!this.formValues.get('owner_email')) {
          this.setError(
            'owner_email',
            i18n.gettext('Please input owner email.')
          );
        } else {
          this.isSavingPayment = true;
          this.createSepaToken();
        }
        break;
      }
      case this.paymentTypes.credit: {
        if (!this.formValues.get('owner_name')) {
          this.setError('owner_name', i18n.gettext('Please input owner name.'));
        } else {
          this.isSavingPayment = true;
          this.createCreditToken();
        }
        break;
      }
      case this.paymentTypes.paypal:
      case this.paymentTypes.bambora: {
        if (!this.formValues.get('token')) {
          this.processPaymentFailure();
        } else {
          this.isSavingPayment = true;
          this.saveForm();
        }
        break;
      }
    }
  };

  @action
  setStripeObject = (stripe) => {
    this.stripe = stripe;
  };

  @action
  setStripeSEPAObject = (stripe) => {
    this.stripeSEPA = stripe;
  };

  @action
  setIbanElement = (ibanElement) => {
    this.ibanElement = ibanElement;
  };

  @action
  createCreditToken = () => {
    this.stripe
      .createPaymentMethod('card', {
        billing_details: { name: this.formValues.get('owner_name') },
      })
      .then(this.processStripePaymentSuccess)
      .catch(this.processPaymentFailure);
  };

  @action
  createSepaToken = () => {
    this.stripeSEPA
      .createPaymentMethod({
        type: 'sepa_debit',
        sepa_debit: this.ibanElement,
        billing_details: {
          name: this.formValues.get('owner_name'),
          email: this.formValues.get('owner_email'),
        },
      })
      .then(this.processStripePaymentSuccess)
      .catch(this.processPaymentFailure);
  };

  @action saveForm = () => {
    const serializedData = this.convertFormToServer();
    this.serverError = null;
    this.view.purchaseDataStore
      .save(serializedData)
      .then(this.savePurchaseSuccess)
      .catch(this.savePurchaseFailure);
  };

  @action savePurchaseSuccess = (data) => {
    if (
      data &&
      data.payment &&
      data.payment.paymentState === 'REQUIRES_USER_ACTION'
    ) {
      this.confirmPaymentIntent(data.payment.additional_info.client_secret);
    } else {
      this.isSavingPayment = false;
      this.setFormValue('ref_num', data.ref_num);
      this.step = this.CONFIRMATION;
    }
  };

  parseError = (error, defaultMess) => {
    if (!(error.response && error.response.data)) {
      return (
        defaultMess ||
        error.message ||
        i18n.gettext('An unexpected error has occurred.')
      );
    }
    if (typeof error.response.data === 'object') {
      switch (error.response.data.error) {
        case 'CUSTOMER_BLOCKED_ERROR':
          return i18n.gettext(
            'Sorry, we do not allow ticket purchases at the moment.'
          );
        default:
          return JSON.stringify(error.response.data);
      }
    }
    return String(error.response.data);
  };

  @action savePurchaseFailure = (error) => {
    this.isSavingPayment = false;
    this.serverError = this.parseError(error);
  };

  @action confirmPaymentIntent = (clientSecret) => {
    this.stripe
      .confirmPaymentIntent(clientSecret)
      .then(this.processStripeIntentConfirmSuccess)
      .catch(this.processPaymentFailure);
  };

  @action handlePaymentIntentNextAction = (clientSecret) => {
    this.stripe
      .handleNextAction({ clientSecret })
      .then(this.processStripeIntentConfirmSuccess)
      .catch(this.processPaymentFailure);
  };

  @action processStripeIntentConfirmSuccess = (result) => {
    if (result.error) {
      const errorMessage =
        result.error.message || DEFAULT_PAYMENT_ERROR_MESSAGE;
      this.processPaymentFailure(errorMessage);
    } else if (result.paymentIntent.next_action) {
      this.handlePaymentIntentNextAction(result.paymentIntent.client_secret);
    } else {
      this.view.purchaseDataStore
        .save({
          id: this.view.lastPurchase.id,
          payment_intent: result.paymentIntent.id,
        })
        .then(this.savePurchaseSuccess)
        .catch(this.savePurchaseFailure);
    }
  };

  @action processStripePaymentSuccess = (result) => {
    if (result.error) {
      this.isSavingPayment = false;
      if (result.error.message) {
        this.serverError = result.error.message;
      } else {
        this.serverError = DEFAULT_PAYMENT_ERROR_MESSAGE;
      }
    } else {
      this.setFormValue('token', result.paymentMethod.id);
      this.saveForm();
    }
  };

  @action processPaymentFailure = (err, defaultMess) => {
    this.isSavingPayment = false;
    this.serverError = this.parseError(err, defaultMess);
  };

  @action handleBamboraCheckout = async (amount) => {
    try {
      this.isCheckingOut = true;
      const result = await new BamboraCheckout(facility.id).checkout(
        amount,
        this.formValues.get('email')
      );
      if (!result.transactionId) {
        return;
      }
      this.formValues.set('token', result.transactionId);
      this.formValues.set('bambora_session_token', result.sessionToken);
    } catch (e) {
      this.processPaymentFailure(
        e,
        i18n.gettext(
          "There's been a problem. Please try again or choose another payment option."
        )
      );
      throw e;
    } finally {
      runInAction(() => {
        this.isCheckingOut = false;
      });
    }
  };
}

export default TicketsBaseFormStore;
